import React, {useEffect, useState} from "react";
import {Button, FormControl, Grid, Paper, TextField, Tooltip} from "@material-ui/core";
import {DatePicker, KeyboardDatePicker, MuiPickersUtilsProvider} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import SingleAsyncAutoComplete from "./SingleAsyncAutoComplete";
import useDataFetchers from "../../../hooks/useDataFetchers";
import _ from "lodash";
import InputComboBox from "../sharedFormComponents/InputComboBox";
import DeleteIcon from "@material-ui/icons/Delete";
import FundedCompanyForm from "../fundedCompanies/FundedCompanyForm";
import AddIcon from "@material-ui/icons/Add";

const PastInvestment = (
    {
        session, pastInvestmentData, index,
        investmentHistory, setInvestmentHistory, investmentStyles,
        investmentStages, handleOnChangeInField, handleOnError, removeInvestmentError,
        isEditing, isNew, deleter, ...props
    }
) => {
    const [trackedInvestment, setTrackedInvestment] = useState({ ...pastInvestmentData })
    const [totalRaised, setTotalRaised] = useState(trackedInvestment.total_raised || '');
    const [amountInvested, setAmountInvested] = useState(trackedInvestment.amount_invested || '');
    const [fundedCompanyError, setFundedCompanyError] = useState(!isEditing);
    const [openFundedCompanyForm, setOpenFundedCompanyForm] = useState(false);
    const [investmentDate, setInvestmentDate] = useState(trackedInvestment.investment_date ? new Date(trackedInvestment.investment_date) : null);

    const fetchers = useDataFetchers(session);

    const requiredErrors = () => {
        return fundedCompanyError || !_.isNumber(trackedInvestment.investment_stage_id)
            || !_.isNumber(trackedInvestment.investment_style_id) || !_.isDate(investmentDate)
    }

    useEffect(() => {
        handleOnError(requiredErrors());
    }, [
        fundedCompanyError, trackedInvestment.investment_stage_id,
        trackedInvestment.investment_style_id, investmentDate
    ]);

    useEffect(() => {
        setInvestmentDate(trackedInvestment.investment_date ? new Date(trackedInvestment.investment_date) : null)
    }, [
        trackedInvestment.investment_date
    ]);

    const handleFundedCompanyChange = (funded_company) => {
        setTrackedInvestment(prevState => ({
            ...prevState,
            funded_company_id: funded_company?.id,
            funded_company_name: funded_company?.name,
        }));
    }

    useEffect(() => {
        if (trackedInvestment._destroy) {
            const newInvestmentHistory = investmentHistory.filter((trackedInvestment, i) => index !== i);
            setInvestmentHistory(newInvestmentHistory)
            removeInvestmentError(index)
        } else {
            let newInvestmentHistory = [...investmentHistory];
            newInvestmentHistory[index] = trackedInvestment;
            setInvestmentHistory(newInvestmentHistory);
        }
    }, [trackedInvestment])

    const handleDateChange = (date) => {
        setTrackedInvestment(prevState => ({
            ...prevState,
            investment_date: date,
        }));
    }

    const handleOnChange = (event) => {
        event.persist();

        setTrackedInvestment(prevState => ({
            ...prevState,
            [event.target.name]: event.target.value,
        }));
    }

    const handleRemoveTrackedInvestment = async () => {
        if (window.confirm('Are you sure you want to delete this investment?')) {
            if (trackedInvestment.id) {
                await deleter.investments(trackedInvestment);
            }

            setTrackedInvestment(prevState => ({
                ...prevState,
                _destroy: true,
            }));
            if (props.setSelectedInvestmentHistory) {
                props.setSelectedInvestmentHistory(null)
            }
        }
    }

    const errorMessage = {};

    const handleCloseForm = () => {
        setOpenFundedCompanyForm(false);
    }

    const RenderPastInvestmentContainer = () => {
        return <Grid container spacing={3} className='admin-investment-history-fields'>
            <Grid item xs={11}>
                <h4>{ isNew && "Investment History Form"}</h4>
            </Grid>
            <Grid item xs={1}>
                <Button onClick={handleRemoveTrackedInvestment} className="error">
                    <DeleteIcon />
                </Button>
            </Grid>


            <Grid item xs={4}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                        disableFuture
                        inputVariant="outlined"
                        label="Investment Date"
                        format="MM/dd/yyyy"
                        value={investmentDate}
                        onChange={handleDateChange}
                        error={!_.isDate(investmentDate)}
                        fullWidth
                        required
                        KeyboardButtonProps={{
                            'aria-label': 'change date',
                        }}
                    />
                </MuiPickersUtilsProvider>
                <span>{errorMessage['investment_date']}</span>
            </Grid>

            <Grid item xs={4}>
                <FormControl fullWidth>
                    <TextField
                        label="Total Raised"
                        variant="outlined"
                        name='total_raised'
                        type='number'
                        inputProps={{ maxLength: 40 }}
                        value={totalRaised}
                        onChange={(event) => setTotalRaised(event.target.value)}
                        onBlur={handleOnChange}
                    />
                    <span>{errorMessage['total_raised']}</span>
                </FormControl>
            </Grid>

            <Grid item xs={4}>
                <FormControl fullWidth>
                    <TextField
                        label="Amount Invested"
                        variant="outlined"
                        name='amount_invested'
                        inputProps={{ maxLength: 40 }}
                        type='number'
                        value={amountInvested}
                        onChange={(event) => setAmountInvested(event.target.value)}
                        onBlur={handleOnChange}
                    />
                    <span>{errorMessage['amount_invested']}</span>
                </FormControl>
            </Grid>

            <Grid item container xs={4} style={{alignItems: "center"}}>
                <Grid xs={10}>
                    <SingleAsyncAutoComplete
                        fetch={fetchers.fundedCompanies}
                        handleOnChange={(value) => handleFundedCompanyChange(value)}
                        handleOnError={(error) => setFundedCompanyError(error)}
                        value={{id: trackedInvestment?.funded_company_id, name: trackedInvestment?.funded_company_name || ''}}
                        field={'funded_company_id'}
                        label={"Funded Company"}
                        required
                    />
                </Grid>

                <Grid xs={2}>
                    <Tooltip title="New Industry Wealth Origin">
                        <Button onClick={() => setOpenFundedCompanyForm(true)} className="create" style={{minWidth: "unset", marginLeft: "8px"}}>
                            <AddIcon />
                        </Button>
                    </Tooltip>
                </Grid>
                <span>{errorMessage['funded_company_id']}</span>
            </Grid>

            <Grid item xs={4}>
                <InputComboBox
                    value={trackedInvestment.investment_style_id || ''}
                    label={'Investment Style'}
                    field={'investment_style_id'}
                    name={'investment_style_id'}
                    handleOnChange={handleOnChange}
                    options={investmentStyles}
                    menuClass={'input-combo-menu'}
                    error={!_.isNumber(trackedInvestment.investment_style_id)}
                    required
                />
            </Grid>

            <Grid item xs={4}>
                <InputComboBox
                    value={trackedInvestment.investment_stage_id || ''}
                    label={'Investment Stage'}
                    field={'investment_stage_id'}
                    name={'investment_stage_id'}
                    handleOnChange={handleOnChange}
                    options={investmentStages}
                    menuClass={'input-combo-menu'}
                    error={!_.isNumber(trackedInvestment.investment_stage_id)}
                    required
                />
            </Grid>

            {
                openFundedCompanyForm && <FundedCompanyForm
                        adminUserSession={session}
                        onClose={handleCloseForm}
                        open={openFundedCompanyForm}
                        afterCreateFundedCompany={handleFundedCompanyChange}
                        onDataUpdate={() => {}}
                        itemIndex={0}
                    />
            }
        </Grid>
    }

    return (
        trackedInvestment._destroy === true ? null :
            <>
                {
                    isNew ? <Paper elevation={3} className={"paper-sub-form"}>
                        {RenderPastInvestmentContainer()}
                    </Paper> : RenderPastInvestmentContainer()
                }
            </>
    )
}

export default PastInvestment